import { Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MultiSiteAwareComponent } from '~/infrastructure/multi-site-aware.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { UserSummary } from '~/models/administration/user';
import { AccountsService } from '~/services/administration.ts/accounts';
import { SecurityService } from '~/services/security.service';
import { LayoutComponent } from '~/shared/components/layout/layout.component';
import { FormatService } from '~/services/administration.ts/format';
import { QuantityFormatsService } from '~/services/administration.ts/quantity-formats';
import { jwtDecode } from "jwt-decode";
import { TokenClaims } from '../models/administration/approle';
import { EnvironmentLoaderService } from '../services/BackEndURL/environment-loader.service';
import { SitesEnum } from '~/shared/enums/sites.enum';

//import { LayoutComponent } from '~/shared/components/layout/layout.component';

@Component({
    selector: 'app-login',
    templateUrl: './login.html',
    styleUrls: ['./login.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class LoginComponent extends MultiSiteAwareComponent implements OnInit {
  _form: FormGroup;
  submitted = false;
  returnUrl: string;
  error: string;
  _verificationCodeMode = false;
  _showPassword = false;
  _agreementRequired = false;
  _agreementSubmitting = false;
  _code: string;
  model: any;
  _selectedAccounts: any[] = [];
  _loggedInAccountSites: string = "";
  _showAccountAndSiteSelection: boolean = false;
  _isMultiSite: boolean = false;
  _currentUser: UserSummary;
  showPassword: boolean = false;
  currentYear: number = new Date().getFullYear();

  get footerUrls() {
    const config = EnvironmentLoaderService.envConfig;
    return {
      releaseNoteUrl: config?.releaseNoteUrl || 'https://asapsystems.com/inventory-asset-tracking-release-notes/',
      contactUsUrl: config?.contactUsUrl || 'https://barcloud.com/company/contact-us/',
      companyUrl: config?.companyUrl || 'https://asapsystems.com/company/barcloud-about-us/',
      returnPolicyUrl: config?.returnPolicyUrl || 'https://barcloud.com/return-policy/',
      privacyPolicyUrl: config?.privacyPolicyUrl || 'https://barcloud.com/privacy-policy/',
      termsOfServiceUrl: config?.termsOfServiceUrl || config?.termsAndConditionsUrl || 'https://barcloud.com/terms-of-service/',
      termsAndConditionsUrl: config?.termsAndConditionsUrl || config?.termsOfServiceUrl || 'https://barcloud.com/terms-of-service/',
    };
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  constructor(private _fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private _securityService: SecurityService,
    private _accountsService: AccountsService,
    private _formatService: FormatService,
    private _layoutComponent: LayoutComponent,
    private _quantityFormatService: QuantityFormatsService) {
    super();
    // redirect to home if already logged in
    if (this._securityService.currentUser || localStorage.getItem('accessToken')) {
      this.router.navigate(['/']);
    }
  }

  ngOnInit() {
    this._form = this._fb.group({
      email: ['', Validators.required],
      password: ['', Validators.required],
    });

    if (this.route.snapshot.queryParams.token != null) {
      let model = {
        token: this.route.snapshot.queryParams.token
      };

      this._securityService.getCustomerUserCredentials(model).subscribe((x) => {
        this._form.get('email').setValue(x.userName);
        this._form.get('password').setValue(x.password);

        this.login();
      });
    }

    // get return url from route parameters or default to '/'
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '';

    if (window.history.state && window.history.state.error) {
      this.error = window.history.state.error;
    }
  }

  _navigate(url: string) {
    this.router.navigate([url]);
  }
  // convenience getter for easy access to form fields
  get form() {
    return this._form.controls;
  }

  onSubmit() {
    if (this._showAccountAndSiteSelection && this._selectedAccounts.length === 0) {
      this._layoutComponent.openPopup({
          title: 'Alert',
          message: 'Please, choose the Site for the desired Account.',
          withoutConfirm: true
        });

      return;
    }

    this.submitted = true;
    this.login();
  }

  login(acceptAgreement = false) {
    if (!this._form.invalid) {
      this.model = this._form.value;
      this.model['validUser'] = false;
      this.model['acceptAgreement'] = acceptAgreement;

      if (localStorage.getItem('enterprise_user_accounts')) {
        let accounts = JSON.parse(localStorage.getItem('enterprise_user_accounts'));

        if ((this.model['validUser'] = accounts.find((x: any) => x === this.model['email']))) {
          this.model['validUser'] = true;
        }
      }

      if (!this._showAccountAndSiteSelection && this._selectedAccounts.length == 0) {
        this._securityService.login(this.model).subscribe((data: any) => {
          this._agreementSubmitting = false;

          if (data.agreementRequired) {
            this._agreementRequired = true;
            return;
          }

          this._agreementRequired = false;

          if (data.tokenExists) {
            if (localStorage.getItem('accessToken') || localStorage.getItem('sessionEnded')) {
              if (!this.model) {
                this._securityService.removeLocalCredentials();
              }
              else {
                this._securityService.removeOldSession(this.model).subscribe(() => {
                  this.login();
                  localStorage.removeItem('sessionEnded')
                });
              }

            } else {
              this.onTokenExists();
            }
          }
          else {
            if (data.verificationCode) {
              this._verificationCodeMode = true;
            }
            else if (data.requiredResetPassword) {
              this.router.navigate(['reset-password'], { queryParams: { token: data.token, email: data.email, isComplex: data.isComplex, resetRequired: true } });
              // this.router.navigateByUrl(['reset-password/token='+ data.token + "&email="+ data.email + "&isComplex="+ data.isComplex ])
            }
            else {
              if (data.accessToken && data.refreshToken) {
                this.loginSuccess(data);
              }
            }
          }
        }, (error: any) => {
          this._agreementSubmitting = false;
          this.error = this.getLoginErrorMessage(error);
        });
      }
      else {
        if (localStorage.getItem('accessToken') && localStorage.getItem('refreshToken')) {
          this._securityService.setCurrentUser().subscribe(() => {
            localStorage.setItem('loggedInAccountSites', this._loggedInAccountSites);
            this.router.navigate([this.returnUrl]);
          });
        }
      }
    }
  }

  acceptAgreement() {
    this._agreementSubmitting = true;
    this.login(true);
  }

  cancelAgreement() {
    this._agreementRequired = false;
    this._agreementSubmitting = false;
    this.model = null;
    this.submitted = false;
    this._form.reset();
    this._securityService.removeLocalCredentials(true);
  }

  _getAccounts = (): Observable<any> => {
    return this._accountsService.GetAccountsSitesDrop();
  }

  onTokenExists() {
    if (!this.model) {
      this._securityService.removeLocalCredentials();
    }
    else {
      if (confirm('You are already logged in! \nAre you sure you want to delete the old session?')) {
        this._securityService.removeOldSession(this.model).subscribe(() => {
          this.login();
        });
      }
    }
  }

  private getLoginErrorMessage(error: any): string {
    const responseMessage = typeof error?.error === 'string'
      ? error.error
      : error?.error?.data || error?.error?.message || error?.message;

    if (responseMessage === 'Account expired') {
      return 'Please contact your administrator.';
    }

    return 'Invalid email or password.';
  }

  onTokenExistsXXXX() {
    if (!this.model) {
      this._securityService.removeLocalCredentials();
    }
    else {
      this._layoutComponent.openPopup({
        title: 'Validation',
        message: 'You are already logged in! \nAre you sure you want to delete the old session?.',
        withoutConfirm: false
      });

      {
        this._securityService.removeOldSession(this.model).subscribe(() => {
          this.login();
        });
      }

      return;
    }
  }

  onSubmitVerificationcode() {
    this.model['verificationCode'] = this._code;

    this._securityService.verifyCode(this.model).subscribe((data: any) => {
      if (data.agreementRequired) {
        this._verificationCodeMode = false;
        this._agreementRequired = true;
        return;
      }

      if (data.tokenExists) {
        this._securityService.removeLocalCredentials();
        this._verificationCodeMode = false;
      }
      else {
        if (data.accessToken && data.refreshToken) {
          this.loginSuccess(data);
        }
      }
    });
  }

  private loginSuccess(data: any) {
    localStorage.setItem('accessToken', JSON.stringify(data.accessToken));
    localStorage.setItem('refreshToken', JSON.stringify(data.refreshToken));

    const decodedToken = jwtDecode<TokenClaims>(data.accessToken.token);

    if (decodedToken.appRoleId == "99991") {
      const shoppingCartUrl = EnvironmentLoaderService.envConfig.shoppingCartUrl;

      window.location.href = `${shoppingCartUrl}?accessToken=${data.accessToken.token}`;

      return;
    }

    this._securityService.setRefreshTokenInterval(data.accessToken.expiresIn);
    this._securityService.initializePermissions();
    this._formatService.getFormats();
    this._quantityFormatService.getQuantityFormatForNumericTextBox();
    this._quantityFormatService.getPipeQuantityFormatSetting();

    // add the email to storage
    if (localStorage.getItem('enterprise_user_accounts')) {
      let accounts = JSON.parse(localStorage.getItem('enterprise_user_accounts'));

      // add the email to storage if not exists
      if (!accounts.find((x: any) => x === this.model.email)) {
        accounts.push(this.model.email);
        localStorage.removeItem('enterprise_user_accounts');
        localStorage.setItem('enterprise_user_accounts', JSON.stringify(accounts));
      }
    }
    else {
      let accounts = [];
      accounts.push(this.model.email);
      localStorage.setItem('enterprise_user_accounts', JSON.stringify(accounts));
    }

    this._securityService.setCurrentUser().subscribe(() => {
      this._currentUser = this._securityService.currentUser;

      this._securityService.getLicense().subscribe((data: any) => {
        this.initializePermissions();

        if (data.general.numberOfSites > 1) {
          this._isMultiSite = true;
        }

        if (this._currentUser != null && !this._currentUser.isEnterprise) {
          if (this._isMultiSite && this._currentUser.userSites.length > 1) { // || this._currentUser.userSites[0].id == null //if(this._currentUser.userSites.length > 0) {
            this._showAccountAndSiteSelection = true;
          }
          else {
            //get account sites, take first
            this._accountsService.GetAccountsSitesDrop().subscribe((data: any) => {
              var _items = data.value ?? data.data?.value;
              if (!_items || _items.length === 0) {
                this._layoutComponent.openPopup({
                title: 'Alert',
                message: 'No account site found.',
                withoutConfirm: true
              });
                return;
              }
              var selectedAccount = _items[0];

              var _userSites = [..._items[0].sites];

              var _selectedAccountsAndSitesArray: any[] = [];
              var _selectedAccountsAndSites: any = { siteId: _userSites[0]?.id ?? null, accountId: selectedAccount.id };

              _selectedAccountsAndSitesArray.push(_selectedAccountsAndSites);
              this._loggedInAccountSites = JSON.stringify(_selectedAccountsAndSitesArray);

              localStorage.setItem('loggedInAccountSites', this._loggedInAccountSites);
              this.router.navigate([this.returnUrl]);
            });
          }
        }
        else {
          this.router.navigate([this.returnUrl]);
        }
      });
    });
  }

  _closeAccountAndSiteWindow(): void {
    this._showAccountAndSiteSelection = false;
  }

  _onAccountRowSelectionChanges(e: any[]) {
    super._onAccountRowSelectionChanges(e, () => {
      this._loggedInAccountSites = JSON.stringify(this._selectedAccounts);
    });
  }

  initializePermissions() {
    this._securityService.initializePermissions();
  }
}
